import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { Search, X } from "lucide-react";
import { cPath } from "../../constants";
import {
  fetchSearchSuggestions,
  clearSearchSuggestions,
} from "../../store/actions/searchActions";

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgb(0 0 0 / 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: clamp(3.5rem, 12vh, 6rem) ${({ theme }) => theme.layout.shellPadX} 1rem;
`;

const Dialog = styled.div`
  position: relative;
  width: min(100%, 40rem);
  max-height: min(85vh, 32rem);
  display: flex;
  flex-direction: column;
  background: hsl(${({ theme }) => theme.colors.card});
  color: hsl(${({ theme }) => theme.colors.foreground});
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  box-shadow: ${({ theme }) => theme.shadow.card};
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid hsl(${({ theme }) => theme.colors.border});
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(1.125rem, 1rem + 0.5vw, 1.375rem);
  font-weight: 700;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: ${({ theme }) => theme.radius.md};
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  &:hover {
    background: hsl(${({ theme }) => theme.colors.muted});
    color: hsl(${({ theme }) => theme.colors.foreground});
  }
`;

const Body = styled.div`
  padding: 1rem 1.25rem 1.25rem;
  overflow: auto;
`;

const Form = styled.form`
  position: relative;
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 1rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  background: hsl(${({ theme }) => theme.colors.muted});
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: clamp(1rem, 0.95rem + 0.2vw, 1.125rem);
  color: hsl(${({ theme }) => theme.colors.foreground});
  &::placeholder {
    color: hsl(${({ theme }) => theme.colors.mutedForeground});
  }
`;

const SubmitBtn = styled.button`
  flex-shrink: 0;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radius.md};
  background: hsl(${({ theme }) => theme.colors.primary});
  color: hsl(${({ theme }) => theme.colors.primaryForeground});
  font-weight: 600;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  &:hover {
    opacity: 0.92;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Dropdown = styled.div`
  margin-top: 0.5rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  background: hsl(${({ theme }) => theme.colors.card});
  overflow: hidden;
  max-height: 14rem;
  overflow-y: auto;
`;

const Row = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  text-align: left;
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
  color: hsl(${({ theme }) => theme.colors.foreground});
  &:hover {
    background: hsl(${({ theme }) => theme.colors.muted});
  }
`;

const RowImg = styled.img`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.25rem;
  object-fit: contain;
`;

const RowTextCol = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  flex: 1;
  text-align: left;
`;

const RowMeta = styled.span`
  font-size: clamp(0.75rem, 0.72rem + 0.12vw, 0.8125rem);
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  font-weight: 400;
`;

const SearchAllBtn = styled.button`
  width: 100%;
  padding: 0.625rem 1rem;
  text-align: left;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.primary});
  border-top: 1px solid hsl(${({ theme }) => theme.colors.border});
  &:hover {
    background: hsl(${({ theme }) => theme.colors.muted});
  }
`;

const EmptyHint = styled.div`
  padding: 0.75rem 1rem;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const Hint = styled.p`
  margin: 0.75rem 0 0;
  font-size: clamp(0.8125rem, 0.78rem + 0.15vw, 0.9375rem);
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

export default function SearchModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const suggestionStores = useSelector((s) => s.search.suggestionStores);
  const suggestionCoupons = useSelector((s) => s.search.suggestionCoupons);
  const suggestionsLoading = useSelector((s) => s.search.suggestionsLoading);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setShowDropdown(false);
      dispatch(clearSearchSuggestions());
      return;
    }
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      cancelAnimationFrame(id);
    };
  }, [isOpen, onClose, dispatch]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const q = query.trim();
    if (!q) {
      dispatch(clearSearchSuggestions());
      return undefined;
    }
    const ac = new AbortController();
    const t = window.setTimeout(() => {
      dispatch(fetchSearchSuggestions(q, 8, ac.signal));
    }, 280);
    return () => {
      window.clearTimeout(t);
      ac.abort();
    };
  }, [isOpen, query, dispatch]);

  const qTrim = query.trim();
  const qLower = query.toLowerCase();

  const filteredStores = useMemo(() => {
    if (!isOpen || !qLower) return [];
    return suggestionStores;
  }, [isOpen, suggestionStores, qLower]);

  const filteredCoupons = useMemo(() => {
    if (!isOpen || !qLower) return [];
    return suggestionCoupons;
  }, [isOpen, suggestionCoupons, qLower]);

  if (!isOpen) return null;

  const q = qTrim;

  const goSearch = (raw) => {
    const t = raw.trim();
    if (!t) return;
    navigate(`${cPath("search")}?q=${encodeURIComponent(t)}`);
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    goSearch(query);
  };

  return (
    <Backdrop
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Head>
          <Title id="search-modal-title">Search</Title>
          <CloseBtn type="button" aria-label="Close search" onClick={onClose}>
            <X size={20} strokeWidth={2} />
          </CloseBtn>
        </Head>
        <Body>
          <Form onSubmit={handleSubmit}>
            <Field>
              <Search size={20} strokeWidth={2} color="hsl(220 10% 46%)" />
              <Input
                ref={inputRef}
                type="search"
                placeholder="Search stores, coupons…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShowDropdown(true);
                }}
                onBlur={() => setTimeout(() => setShowDropdown(false), 180)}
                onFocus={() => query && setShowDropdown(true)}
              />
              <SubmitBtn type="submit" disabled={!q}>
                Go
              </SubmitBtn>
            </Field>
            {showDropdown && query && (
              <Dropdown>
                {suggestionsLoading && <EmptyHint>Searching…</EmptyHint>}
                {!suggestionsLoading &&
                  filteredStores.slice(0, 6).map((store) => (
                    <Row
                      key={store.id}
                      type="button"
                      onClick={() => {
                        navigate(cPath(`store/${store.slug}`));
                        onClose();
                      }}
                    >
                      <RowImg src={store.logo || store.image} alt="" />
                      {store.name}
                    </Row>
                  ))}
                {!suggestionsLoading &&
                  filteredCoupons.slice(0, 6).map((coupon) => (
                    <Row
                      key={coupon.id}
                      type="button"
                      onClick={() => {
                        navigate(cPath(`coupon/${coupon.id}`));
                        onClose();
                      }}
                    >
                      <RowTextCol>
                        <span>{coupon.title}</span>
                        {coupon.storeName ? (
                          <RowMeta>{coupon.storeName}</RowMeta>
                        ) : null}
                      </RowTextCol>
                    </Row>
                  ))}
                {!suggestionsLoading &&
                  filteredStores.length === 0 &&
                  filteredCoupons.length === 0 && (
                    <EmptyHint>No matching stores or coupons</EmptyHint>
                  )}
                <SearchAllBtn type="button" onClick={() => goSearch(query)}>
                  Search all for &quot;{query.trim()}&quot;
                </SearchAllBtn>
              </Dropdown>
            )}
          </Form>
          <Hint>Tip: press Enter to search, Esc to close.</Hint>
        </Body>
      </Dialog>
    </Backdrop>
  );
}
